import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Sun, X, Sparkles } from 'lucide-react';
import { useGardenSettings } from '../../context/GardenSettingsContext';
import { useShadowHeatmap } from '../../context/ShadowHeatmapContext';
import { useGardenTime, withMonth } from '../../context/GardenTimeContext';
import { serializeCanvasObjects } from '../../hooks/useGardenPersistence';
import { buildSceneModel } from '../../scene3d/buildSceneModel';
import { createShadowTerrain } from '../../scene3d/terrain';
import { getSunPosition, getSunDirection3D } from '../../scene3d/sunlight';
import { computeShadowGeometry } from '../../scene3d/shadow/shadowCaster';
import useSunCoordinates from '../../hooks/useSunCoordinates';
import SunTimeControls from './SunTimeControls';
import ShadowOverlay from './ShadowOverlay';
import HeatmapOverlay from './HeatmapOverlay';
import HeatmapLegend from './HeatmapLegend';

/**
 * SunShadowPanel - Panel-Inhalt „Sonne & Schatten" (#605/#606, Andocken #633)
 *
 * Selbsttragender Panel-Inhalt ohne Positions-Wissen (Desktop: Seitenleiste,
 * Tablet/Mobile: FloatingViewPanel). Gemountet = offen — SceneModel-Aufbau,
 * Momentschatten-Overlay und Heatmap laufen, solange das Panel offen ist;
 * die Overlays hängen nur am Fabric-Canvas, nicht an der DOM-Position.
 *
 * Modi: Momentaufnahme (#605, wandernder Schatten) | Jahresübersicht (#606,
 * Sonnenstunden-Heatmap auf Berechnen-Klick, Web Worker + Cache im
 * ShadowHeatmapContext — überlebt Panel-Schließen).
 *
 * Der Monats-Anteil des Sonnenstands ist der geteilte Garten-Monat (#608);
 * Uhrzeit/Tag bleiben Schatten-lokal.
 */

/** Standard-Startzeitpunkt: heute, 12:00 Ortszeit. */
function defaultSunDate() {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  return d;
}

const SunShadowPanel = ({ canvas, onClose }) => {
  const { settings } = useGardenSettings();
  const heatmap = useShadowHeatmap();
  const { month, setMonth } = useGardenTime();
  const [mode, setMode] = useState('moment'); // 'moment' | 'year'
  const [sunDate, setSunDate] = useState(defaultSunDate);

  // Geteilter Garten-Monat ↔ Sonnenstand-Monat (#608): wirksamer Zeitpunkt
  // wird beim Rendern aus Kontext-Monat + lokalem Tag/Uhrzeit abgeleitet.
  const effectiveSunDate = useMemo(
    () => (sunDate.getMonth() === month ? sunDate : withMonth(sunDate, month)),
    [sunDate, month]
  );

  // Push: Datum-/Preset-Wechsel im Schatten-Panel meldet den Monat zurück.
  const handleSunDateChange = (next) => {
    setSunDate(next);
    if (next.getMonth() !== month) setMonth(next.getMonth());
  };

  const active = !!canvas;
  const coords = useSunCoordinates({ active });

  // SceneModel aus dem Canvas-Snapshot; neu bauen bei Struktur-Änderungen.
  const [geometryVersion, setGeometryVersion] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const bump = () => setGeometryVersion((v) => v + 1);
    canvas.on('object:added', bump);
    canvas.on('object:removed', bump);
    canvas.on('object:modified', bump);
    return () => {
      canvas.off('object:added', bump);
      canvas.off('object:removed', bump);
      canvas.off('object:modified', bump);
    };
  }, [active, canvas]);

  const sceneModel = useMemo(() => {
    if (!active) return null;
    return buildSceneModel(serializeCanvasObjects(canvas), settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- geometryVersion triggert Neuaufbau
  }, [active, canvas, settings, geometryVersion]);

  // Terrain-Sampler für plateau-genaue Schatten (#631); null = flacher Garten.
  const shadowTerrain = useMemo(
    () => createShadowTerrain(sceneModel?.meta?.terrainSpec),
    [sceneModel]
  );

  // Momentschatten-Geometrie (nur im Momentaufnahme-Modus gebraucht)
  const groups = useMemo(() => {
    if (mode !== 'moment' || !sceneModel || !coords) return [];
    const { azimuth, altitude } = getSunPosition(
      coords.latitude,
      coords.longitude,
      effectiveSunDate
    );
    const sunDirection = getSunDirection3D(azimuth, altitude, settings.northAngle ?? 0);
    return computeShadowGeometry(sceneModel, {
      sunDirection,
      month: effectiveSunDate.getMonth(),
      terrain: shadowTerrain,
    }).groups;
  }, [mode, sceneModel, coords, effectiveSunDate, settings.northAngle, shadowTerrain]);

  const heatmapStale =
    mode === 'year' && sceneModel && coords ? heatmap.isStale(sceneModel, coords) : false;

  const runHeatmap = () => {
    if (sceneModel && coords) heatmap.compute(sceneModel, coords);
  };

  return (
    <>
      {active && mode === 'moment' && <ShadowOverlay canvas={canvas} groups={groups} />}
      {active && mode === 'year' && heatmap.grid && (
        <HeatmapOverlay canvas={canvas} grid={heatmap.grid} />
      )}

      <div>
        <div className="flex items-center gap-2">
          <Sun className="h-4 w-4 text-amber-500" aria-hidden="true" />
          <span className="text-sm font-semibold text-gray-800">Sonne &amp; Schatten</span>
          <button
            type="button"
            aria-label="Schließen"
            onClick={onClose}
            className="ml-auto rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {/* Modus-Schalter */}
        <div
          role="group"
          aria-label="Ansicht"
          className="mt-2 inline-flex rounded-md border border-gray-200 p-0.5 text-xs"
        >
          {[
            { id: 'moment', label: 'Momentaufnahme' },
            { id: 'year', label: 'Jahresübersicht' },
          ].map((m) => (
            <button
              key={m.id}
              type="button"
              aria-pressed={mode === m.id}
              onClick={() => setMode(m.id)}
              className={`rounded px-2 py-1 font-medium transition-colors ${
                mode === m.id
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* Modus-Inhalt */}
        <div className="mt-2">
          {!coords ? (
            <p className="text-xs text-gray-500">Standort wird bestimmt …</p>
          ) : mode === 'moment' ? (
            <SunTimeControls
              date={effectiveSunDate}
              latitude={coords.latitude}
              longitude={coords.longitude}
              onChange={handleSunDateChange}
              className="border-0 shadow-none p-0 bg-transparent"
            />
          ) : (
            <div>
              {heatmap.status === 'computing' ? (
                <div>
                  <p className="text-xs text-gray-600">Sonnenstunden werden berechnet …</p>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-gray-100">
                    <div
                      className="h-full bg-emerald-500 transition-all"
                      style={{ width: `${Math.round(heatmap.progress * 100)}%` }}
                    />
                  </div>
                </div>
              ) : heatmap.grid ? (
                <>
                  <HeatmapLegend />
                  {heatmapStale && (
                    <p className="mt-2 text-xs text-amber-700">
                      Der Plan hat sich geändert — neu berechnen für aktuelle Zonen.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-xs leading-relaxed text-gray-500">
                  Zeigt, wie viel Sonne jede Stelle über das Jahr bekommt —
                  vollsonnig, halbschattig oder schattig.
                </p>
              )}

              <button
                type="button"
                onClick={runHeatmap}
                disabled={heatmap.status === 'computing' || !sceneModel}
                className="mt-2 inline-flex min-h-[36px] items-center justify-center rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {heatmap.grid ? 'Neu berechnen' : 'Berechnen'}
              </button>
            </div>
          )}
        </div>

        {coords?.source === 'fallback' && (
          <p className="mt-2 text-xs leading-relaxed text-gray-500">
            Standort ungenau — auf die Mitte Deutschlands geschätzt. Prüfe die
            Postleitzahl in den Einstellungen.
          </p>
        )}

        <p className="mt-2 flex items-center gap-1 text-xs text-emerald-700">
          <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
          Premium-Vorschau — frei ausprobieren, dein Plan bleibt unberührt.
        </p>
      </div>
    </>
  );
};

SunShadowPanel.propTypes = {
  canvas: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

SunShadowPanel.defaultProps = {
  canvas: null,
};

export default SunShadowPanel;
